import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import PollingAPI from "../serviceAPI/PollingAPI";
import { PollingScreenStringLabels } from "../util/Strings";

const initialState = {
  pollsForVisit: [],
  loadedVisitId: null,
  fetchStatus: "idle",
  saveStatus: "idle",
  deleteStatus: "idle",
  resultsStatus: "idle",
  resultsSummary: null,
  resultsError: null,
  resultsSocketStatus: "idle",
  error: null,
  lastMessage: null,
};

function mergePollIntoList(list, poll) {
  const next = Array.isArray(list) ? [...list] : [];
  const idx = next.findIndex((p) => p._id === poll._id);
  if (idx >= 0) {
    next[idx] = poll;
  } else {
    next.push(poll);
  }
  return next.sort((a, b) => {
    const ta = new Date(a.updatedAt || 0).getTime();
    const tb = new Date(b.updatedAt || 0).getTime();
    return tb - ta;
  });
}

export const fetchPollForVisit = createAsyncThunk(
  "polling/fetchByVisit",
  async (visitId, { rejectWithValue }) => {
    try {
      const { data } = await PollingAPI.getByVisitId(visitId);
      return Array.isArray(data) ? data : data ? [data] : [];
    } catch (error) {
      return rejectWithValue(error.message || PollingScreenStringLabels.errorFetchFailed);
    }
  }
);

export const fetchPollResults = createAsyncThunk(
  "polling/fetchResults",
  async ({ visitId, pollId }, { rejectWithValue }) => {
    try {
      const { data } = await PollingAPI.getResults(visitId, pollId);
      return data?.resultsSummary || null;
    } catch (error) {
      return rejectWithValue({
        message: error.message || PollingScreenStringLabels.errorResultsFailed,
        status: error.status,
      });
    }
  }
);

export const persistPoll = createAsyncThunk(
  "polling/persist",
  async (
    { visitId, pollId, name, questions, status, showPollResults, previousStatus },
    { rejectWithValue }
  ) => {
    try {
      const nextStatus = pollId ? status || "draft" : "draft";
      const body = {
        name,
        questions,
        status: nextStatus,
        showPollResults: Boolean(showPollResults),
      };

      if (pollId && previousStatus === "live" && nextStatus === "closed") {
        const { data, visitPolls } = await PollingAPI.close(visitId, pollId);
        return { poll: data, operation: "closed", visitPolls };
      }

      if (pollId && previousStatus !== "draft") {
        return rejectWithValue({
          message: PollingScreenStringLabels.errorPollNotEditable,
          status: 409,
        });
      }

      if (pollId) {
        const { data, visitPolls } = await PollingAPI.update(pollId, visitId, body);
        return { poll: data, operation: "updated", visitPolls };
      }

      const { data, visitPolls } = await PollingAPI.create(visitId, body);
      return { poll: data, operation: "created", visitPolls };
    } catch (error) {
      return rejectWithValue({
        message: error.message || PollingScreenStringLabels.errorSaveFailed,
        status: error.status,
      });
    }
  }
);

export const deletePoll = createAsyncThunk(
  "polling/delete",
  async ({ visitId, pollId, previousStatus }, { rejectWithValue }) => {
    if (previousStatus === "live") {
      return rejectWithValue({
        message: PollingScreenStringLabels.errorDeleteLive,
        status: 409,
      });
    }
    try {
      const { visitPolls } = await PollingAPI.deletePoll(pollId, visitId);
      return { pollId, visitPolls };
    } catch (error) {
      return rejectWithValue({
        message: error.message || PollingScreenStringLabels.errorDeleteFailed,
        status: error.status,
      });
    }
  }
);

const PollingSlice = createSlice({
  name: "polling",
  initialState,
  reducers: {
    clearPollingMessages: (state) => {
      state.lastMessage = null;
      state.error = null;
    },
    clearPollResults: (state) => {
      state.resultsSummary = null;
      state.resultsStatus = "idle";
      state.resultsError = null;
      state.resultsSocketStatus = "idle";
    },
    receivePollResultsFromSocket: (state, action) => {
      state.resultsSummary = action.payload;
      state.resultsStatus = "succeeded";
      state.resultsError = null;
    },
    setPollResultsSocketStatus: (state, action) => {
      state.resultsSocketStatus = action.payload || "idle";
    },
    resetPollingVisitState: (state) => {
      state.pollsForVisit = [];
      state.loadedVisitId = null;
      state.fetchStatus = "idle";
      state.saveStatus = "idle";
      state.deleteStatus = "idle";
      state.resultsStatus = "idle";
      state.resultsSummary = null;
      state.resultsError = null;
      state.resultsSocketStatus = "idle";
      state.error = null;
      state.lastMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPollForVisit.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
        state.lastMessage = null;
        state.loadedVisitId = null;
        if (state.pollsForVisit.length) {
          state.pollsForVisit = [];
        }
      })
      .addCase(fetchPollForVisit.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        state.loadedVisitId = action.meta.arg;
        state.pollsForVisit = Array.isArray(action.payload) ? action.payload : [];
        state.lastMessage = null;
      })
      .addCase(fetchPollForVisit.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload || PollingScreenStringLabels.errorFetchFailed;
      })
      .addCase(fetchPollResults.pending, (state) => {
        state.resultsError = null;
        if (!state.resultsSummary) {
          state.resultsStatus = "loading";
        }
      })
      .addCase(fetchPollResults.fulfilled, (state, action) => {
        state.resultsStatus = "succeeded";
        state.resultsSummary = action.payload;
      })
      .addCase(fetchPollResults.rejected, (state, action) => {
        const payload = action.payload;
        if (payload && typeof payload === "object") {
          state.resultsError = payload.message;
        } else {
          state.resultsError = payload || PollingScreenStringLabels.errorResultsFailed;
        }

        if (!state.resultsSummary) {
          state.resultsStatus = "failed";
          state.resultsSummary = null;
        }
      })
      .addCase(persistPoll.pending, (state) => {
        state.saveStatus = "loading";
        state.error = null;
        state.lastMessage = null;
      })
      .addCase(persistPoll.fulfilled, (state, action) => {
        state.saveStatus = "succeeded";
        const list = action.payload.visitPolls;
        state.pollsForVisit = Array.isArray(list)
          ? [...list].sort((a, b) => {
              const ta = new Date(a.updatedAt || 0).getTime();
              const tb = new Date(b.updatedAt || 0).getTime();
              return tb - ta;
            })
          : mergePollIntoList(state.pollsForVisit, action.payload.poll);
        if (action.payload.operation === "created") {
          state.lastMessage = PollingScreenStringLabels.pollCreatedSuccess;
        } else if (action.payload.operation === "closed") {
          state.lastMessage = PollingScreenStringLabels.pollClosedSuccess;
        } else {
          state.lastMessage = PollingScreenStringLabels.pollUpdatedSuccess;
        }
      })
      .addCase(persistPoll.rejected, (state, action) => {
        state.saveStatus = "failed";
        const payload = action.payload;
        if (payload && typeof payload === "object" && payload.message) {
          state.error = payload.message;
        } else if (typeof payload === "string") {
          state.error = payload;
        } else {
          state.error = PollingScreenStringLabels.errorSaveFailed;
        }
      })
      .addCase(deletePoll.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
        state.lastMessage = null;
      })
      .addCase(deletePoll.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.pollsForVisit = Array.isArray(action.payload.visitPolls)
          ? action.payload.visitPolls
          : state.pollsForVisit.filter((poll) => poll._id !== action.payload.pollId);
        state.resultsSummary = null;
        state.resultsStatus = "idle";
        state.resultsError = null;
        state.lastMessage = PollingScreenStringLabels.deletePollSuccess;
      })
      .addCase(deletePoll.rejected, (state, action) => {
        state.deleteStatus = "failed";
        const payload = action.payload;
        if (payload && typeof payload === "object" && payload.message) {
          state.error = payload.message;
        } else if (typeof payload === "string") {
          state.error = payload;
        } else {
          state.error = PollingScreenStringLabels.errorDeleteFailed;
        }
      });
  },
});

export const {
  clearPollingMessages,
  clearPollResults,
  resetPollingVisitState,
  receivePollResultsFromSocket,
  setPollResultsSocketStatus,
} = PollingSlice.actions;
export default PollingSlice.reducer;
